/**
 * In-app browser detection + escape for the login page (2026-09-22).
 *
 * Google refuses OAuth inside embedded webviews (policy `disallowed_useragent`),
 * so a staff member who taps a Hub link inside LINE / Facebook / Instagram /
 * TikTok / WeChat cannot sign in there — nothing on our side can make Google
 * sign-in work inside those apps. The login page uses this module to get the
 * person OUT of the app and into Safari / Chrome (PO: jump out to the regular
 * browser), and shows an "open in Safari / Chrome" card while it tries.
 *
 * Only apps whose user-agent marker is reliable are listed: a false positive
 * would hide the Google button from a real browser and lock that person out.
 * WhatsApp is deliberately absent — it opens links in SFSafariViewController /
 * Chrome Custom Tabs, which Google allows and which carry no WhatsApp marker.
 */

export type InAppBrowser = 'line' | 'messenger' | 'facebook' | 'instagram' | 'tiktok' | 'wechat';

export type MobilePlatform = 'ios' | 'android' | 'other';

/** Query flag LINE honours by opening the URL in the device's default browser. */
export const OPEN_EXTERNAL_BROWSER_PARAM = 'openExternalBrowser';

/**
 * Marks "the Android Chrome hand-off was already tried". Carried on the intent's
 * fallback URL, so if Chrome is missing and the app reloads us, we don't retry.
 */
export const ESCAPE_TRIED_PARAM = 'inAppEscape';

// Order matters: Messenger's UA also carries FBAN/FB_IAB, so it is tested
// before the generic Facebook pattern. `\bLine\/\d` needs the slash + digit so
// "Linux" in desktop / Android UAs never matches. TikTok's webview identifies
// itself as `musical_ly_…` / `BytedanceWebview/…` (and sometimes `TikTok`).
const PATTERNS: ReadonlyArray<readonly [InAppBrowser, RegExp]> = [
  ['line', /\bLine\/\d/i],
  ['instagram', /\bInstagram\b/i],
  ['messenger', /\bMessenger|\bFB_IAB\/MESSENGER/i],
  ['facebook', /\bFBAN\/|\bFBAV\/|\bFB_IAB\/|\bFBIOS\b/i],
  ['tiktok', /\bmusical_ly|\bBytedanceWebview\b|\bTikTok\b/i],
  ['wechat', /\bMicroMessenger\//i],
];

export function detectInAppBrowser(userAgent: string | null | undefined): InAppBrowser | null {
  if (!userAgent) return null;
  for (const [app, pattern] of PATTERNS) {
    if (pattern.test(userAgent)) return app;
  }
  return null;
}

export function detectMobilePlatform(userAgent: string | null | undefined): MobilePlatform {
  if (!userAgent) return 'other';
  if (/\biPhone\b|\biPad\b|\biPod\b/i.test(userAgent)) return 'ios';
  if (/\bAndroid\b/i.test(userAgent)) return 'android';
  return 'other';
}

/**
 * The same URL with `openExternalBrowser=1` added, or `null` when the flag is
 * already present — that null is what stops a reload loop if LINE ignores the
 * flag and loads the page in its own browser again.
 */
export function buildExternalBrowserUrl(href: string): string | null {
  const url = new URL(href);
  if (url.searchParams.has(OPEN_EXTERNAL_BROWSER_PARAM)) return null;
  url.searchParams.set(OPEN_EXTERNAL_BROWSER_PARAM, '1');
  return url.toString();
}

/** The current URL without our own escape flags — what the real browser should load. */
function stripEscapeFlags(href: string): URL {
  const url = new URL(href);
  url.searchParams.delete(OPEN_EXTERNAL_BROWSER_PARAM);
  url.searchParams.delete(ESCAPE_TRIED_PARAM);
  return url;
}

/**
 * Android intent URL that asks the OS to open this page in Chrome. Facebook,
 * Instagram and TikTok's Android webviews generally hand `intent:` links to the
 * OS. If Chrome is not installed, Android loads the fallback — this same page
 * with `inAppEscape=1`, so the automatic attempt is not repeated.
 */
export function buildAndroidChromeIntentUrl(href: string): string {
  const target = stripEscapeFlags(href);
  const fallback = new URL(target.toString());
  fallback.searchParams.set(ESCAPE_TRIED_PARAM, '1');
  const hostPath = `${target.host}${target.pathname}${target.search}`;
  return (
    `intent://${hostPath}#Intent;scheme=https;package=com.android.chrome;` +
    `S.browser_fallback_url=${encodeURIComponent(fallback.toString())};end`
  );
}

/**
 * `x-safari-https://…` opens the URL in Safari on iOS 17+. It is undocumented
 * and an app may block it, so it is only ever offered as a button the person
 * taps — if it does nothing, the card's steps and copy link are still there.
 */
export function buildIosSafariUrl(href: string): string {
  const target = stripEscapeFlags(href);
  return `x-safari-https://${target.host}${target.pathname}${target.search}${target.hash}`;
}

export interface InAppEscapePlan {
  /** Navigate here automatically on load (at most once), or null. */
  autoUrl: string | null;
  /** A tap-to-open button for the card, or null when the app blocks every hand-off. */
  button: { browser: 'chrome' | 'safari'; url: string } | null;
}

/**
 * What the login page should do for a given in-app browser.
 *
 * - LINE: its own `openExternalBrowser=1` flag (official, both platforms).
 * - Facebook / Messenger / Instagram / TikTok on Android: an automatic Chrome
 *   intent, once. On iOS no app allows an automatic jump — Safari button only.
 * - WeChat blocks both hand-offs, so it gets the card's manual steps only.
 *
 * `alreadyTried` covers the session-level guard (the page came back after an
 * attempt, e.g. via the back button), so an automatic jump never loops.
 */
export function planInAppEscape(
  app: InAppBrowser,
  platform: MobilePlatform,
  href: string,
  alreadyTried: boolean,
): InAppEscapePlan {
  const button: InAppEscapePlan['button'] =
    platform === 'android'
      ? { browser: 'chrome', url: buildAndroidChromeIntentUrl(href) }
      : platform === 'ios'
        ? { browser: 'safari', url: buildIosSafariUrl(href) }
        : null;

  if (app === 'wechat') return { autoUrl: null, button: null };

  if (app === 'line') {
    return { autoUrl: alreadyTried ? null : buildExternalBrowserUrl(href), button };
  }

  const tried = alreadyTried || new URL(href).searchParams.has(ESCAPE_TRIED_PARAM);
  const autoUrl = platform === 'android' && !tried ? buildAndroidChromeIntentUrl(href) : null;
  return { autoUrl, button };
}

/**
 * Pages that must stay INSIDE the in-app browser. `/link-line` is the employee
 * LINE binding flow: LINE Login signs the person in silently inside LINE, and
 * its `?status=` callback page lands there too. Pushing it out to Chrome would
 * make them sign in to LINE again for no reason.
 */
const ESCAPE_EXEMPT_PATH_PREFIXES: readonly string[] = ['/link-line'];

export function isEscapeExemptPath(pathname: string): boolean {
  return ESCAPE_EXEMPT_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Session guard for the automatic jump, shared by app boot and the login page:
 * if the hand-off fails and the person comes back (back button / app reload),
 * don't fire it again. Storage can be unavailable in webviews — then the URL
 * flags are the only guard. Declared here, not in a component, so it is always
 * initialised before first use.
 */
export const ESCAPE_TRIED_STORAGE_KEY = 'oovi.inAppEscapeTried';

export function readEscapeTried(): boolean {
  try {
    return sessionStorage.getItem(ESCAPE_TRIED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function markEscapeTried(): void {
  try {
    sessionStorage.setItem(ESCAPE_TRIED_STORAGE_KEY, '1');
  } catch {
    // ignore — see readEscapeTried
  }
}

/**
 * True when this visit is inside an in-app browser and the page is not one
 * that must stay there. The router then shows the login page's
 * 「請改用瀏覽器開啟」 card for every page, signed in or not — the Hub is not
 * used inside in-app browsers (PO 2026-09-22).
 */
export function isConfinedToInAppNotice(pathname: string): boolean {
  return detectInAppBrowser(navigator.userAgent) !== null && !isEscapeExemptPath(pathname);
}

/**
 * Runs at app boot, before auth or routing, on EVERY page — not only the login
 * page. A person already signed in inside LINE's browser never reaches the login
 * page, so a login-page-only check left them stuck in LINE (PO's Android phone,
 * 2026-09-22).
 *
 * The app keeps booting afterwards, whatever happens. On Android, LINE can open
 * the page in the default browser WITHOUT navigating its own window — a boot
 * that stopped here left that window blank purple (PO's phone, build 680). What
 * the leftover window shows instead is the router's job (isConfinedToInAppNotice).
 */
export function escapeInAppBrowserOnBoot(): void {
  const app = detectInAppBrowser(navigator.userAgent);
  if (!app || isEscapeExemptPath(window.location.pathname)) return;
  const plan = planInAppEscape(
    app,
    detectMobilePlatform(navigator.userAgent),
    window.location.href,
    readEscapeTried(),
  );
  if (!plan.autoUrl) return;
  markEscapeTried();
  if (app === 'line') {
    // A real top-level load — LINE only reacts when IT loads a URL carrying the
    // flag. router.replace / history.replaceState would not work.
    window.location.replace(plan.autoUrl);
    return;
  }
  window.location.href = plan.autoUrl;
}

/**
 * The address to show / copy on the fallback card: the page the person was
 * actually heading to (the login guard's `?redirect=`), not the login URL with
 * its query noise. Opening it in Safari runs the normal guard → login → back to
 * that page. Same open-redirect guard as the router: same-origin paths only.
 */
export function buildShareableUrl(href: string): string {
  const url = new URL(href);
  const redirect = url.searchParams.get('redirect');
  const path =
    redirect && redirect.startsWith('/') && !redirect.startsWith('//') && redirect !== '/login'
      ? redirect
      : '/';
  return `${url.origin}${path}`;
}
